/**
 * Copy of the Google / Apple sign-in errors (plan 50 § 3.4 codes). Spanish is
 * what speak's UI ships today; English is kept in parallel (i18n-ready rule) and
 * picked by `SOCIAL_COPY_LOCALE` once the UI gets a language switch.
 *
 * Cancelling never reaches here: the native flows resolve `null` and the web
 * callback's `oauth_cancelled` maps to no banner (`socialLoginErrorMessage`
 * returns `null`).
 */
import type { SocialProvider } from '../../lib/native/socialLogin.js';

export type SocialCopyLocale = 'es' | 'en';

/** The language of speak's UI (Spanish-only today, like the rest of the auth screens). */
export const SOCIAL_COPY_LOCALE: SocialCopyLocale = 'es';

/** Where the error happened: the Apple-without-email fix differs per platform. */
export type SocialSurface = 'ios' | 'android' | 'web';

interface SocialCopy {
  providerName: Record<SocialProvider, string>;
  invalidToken: (p: string) => string;
  emailUnverified: (p: string) => string;
  /** iOS: the Apple ID lives in the iPhone's Settings. */
  appleNoEmailIos: string;
  /** Android and web: the Apple ID is managed at account.apple.com. */
  appleNoEmailWeb: string;
  flowExpired: string;
  rateLimited: string;
  providerNetwork: (p: string) => string;
  serverNetwork: string;
  misconfigured: (p: string) => string;
  /** Any other failure with a known provider. */
  failed: (p: string) => string;
  /** Any failure whose provider we do not know (web callback without our marker). */
  generic: string;
}

export const SOCIAL_LOGIN_COPY: Record<SocialCopyLocale, SocialCopy> = {
  es: {
    providerName: { google: 'Google', apple: 'Apple' },
    invalidToken: (p) => `No pudimos verificar tu cuenta de ${p}. Probá de nuevo.`,
    emailUnverified: (p) =>
      `Tu cuenta de ${p} no tiene el email verificado. Verificalo en ${p} o entrá con tu email.`,
    appleNoEmailIos:
      'Apple no nos compartió tu email. En Ajustes › tu nombre › Iniciar sesión con Apple, quitá Orborix Speak y probá de nuevo, o entrá con tu email.',
    appleNoEmailWeb:
      'Apple no nos compartió tu email. En account.apple.com › Iniciar sesión con Apple, quitá Orborix Speak y probá de nuevo, o entrá con tu email.',
    flowExpired: 'El ingreso con Apple venció antes de terminar. Probá de nuevo.',
    rateLimited: 'Demasiados intentos, probá en un minuto.',
    providerNetwork: (p) => `No pudimos conectar con ${p}. Probá de nuevo en un momento.`,
    serverNetwork: 'No pudimos conectar con el servidor. Revisá tu conexión.',
    misconfigured: (p) => `El ingreso con ${p} no está disponible ahora. Entrá con tu email.`,
    failed: (p) => `No pudimos completar el ingreso con ${p}. Probá de nuevo o entrá con tu email.`,
    generic: 'No pudimos completar el ingreso. Probá de nuevo o entrá con tu email.',
  },
  en: {
    providerName: { google: 'Google', apple: 'Apple' },
    invalidToken: (p) => `We couldn't verify your ${p} account. Please try again.`,
    emailUnverified: (p) =>
      `Your ${p} account's email isn't verified. Verify it with ${p} or sign in with your email.`,
    appleNoEmailIos:
      "Apple didn't share your email with us. In Settings › your name › Sign in with Apple, remove Orborix Speak and try again, or sign in with your email.",
    appleNoEmailWeb:
      "Apple didn't share your email with us. At account.apple.com › Sign in with Apple, remove Orborix Speak and try again, or sign in with your email.",
    flowExpired: 'Signing in with Apple expired before it finished. Please try again.',
    rateLimited: 'Too many attempts, try again in a minute.',
    providerNetwork: (p) => `We couldn't reach ${p}. Please try again in a moment.`,
    serverNetwork: "We couldn't reach the server. Check your connection.",
    misconfigured: (p) => `Signing in with ${p} isn't available right now. Use your email.`,
    failed: (p) => `We couldn't finish signing in with ${p}. Try again or use your email.`,
    generic: "We couldn't finish signing you in. Try again or use your email.",
  },
};

/**
 * Banner text for a sign-in error, or `null` when nothing should show (the user
 * cancelled). `code` is the backend's (`invalid_token`, …), the auth-client's
 * (`network_error`, `malformed_response`), the Android return link's, or the web
 * callback's `oauth_<code>` / legacy `oauth`.
 */
export function socialLoginErrorMessage(
  code: string,
  provider: SocialProvider | null,
  surface: SocialSurface,
  locale: SocialCopyLocale = SOCIAL_COPY_LOCALE,
): string | null {
  const copy = SOCIAL_LOGIN_COPY[locale];
  // The web callback prefixes its codes (`/login?error=oauth_<code>`); the
  // native endpoints do not, except `oauth_network` / `oauth_misconfigured`.
  const bare =
    code.startsWith('oauth_') && code !== 'oauth_network' && code !== 'oauth_misconfigured'
      ? code.slice('oauth_'.length)
      : code;
  if (bare === 'cancelled') return null;
  if (bare === 'rate_limited') return copy.rateLimited;
  if (bare === 'network_error') return copy.serverNetwork;
  // Only Apple withholds the email, so the copy is right even without the provider.
  if (bare === 'apple_no_email') {
    return surface === 'ios' ? copy.appleNoEmailIos : copy.appleNoEmailWeb;
  }
  if (bare === 'flow_expired') return copy.flowExpired;
  if (!provider) return copy.generic;
  const p = copy.providerName[provider];
  switch (bare) {
    case 'invalid_token':
      return copy.invalidToken(p);
    case 'email_unverified':
      return copy.emailUnverified(p);
    case 'oauth_network':
      return copy.providerNetwork(p);
    case 'oauth_misconfigured':
      return copy.misconfigured(p);
    default:
      return copy.failed(p);
  }
}
